<template>
    <div class="dashboard-shell">
        <section class="hero-panel">
            <div>
                <p class="eyebrow">Dashboard overview</p>
                <h1 class="text-white">Welcome back, Admin</h1>
                <p class="hero-copy">
                    Track membership, applications, bookings, and tournaments from one clean
                    snapshot.
                </p>
            </div>

            <div class="hero-meta">
                <div class="status-pill" :class="{ loading: isLoading }">
                    {{ isLoading ? "Refreshing analytics" : "Live analytics" }}
                </div>
                <div class="sync-card">
                    <span>Last synced</span>
                    <strong>{{ lastSyncedLabel }}</strong>
                </div>
            </div>
        </section>

        <div v-if="errorMessage" class="status-banner error">
            {{ errorMessage }}
        </div>
        <div v-else-if="isLoading" class="status-banner loading">
            Loading dashboard analytics...
        </div>

        <section class="card-grid" aria-label="Key metrics">
            <article v-for="card in cards" :key="card.title" class="metric-card">
                <div class="metric-card__top">
                    <div class="icon-shell">
                        <component :is="card.icon" class="icon" />
                    </div>
                    <span class="metric-tag">{{ card.tag }}</span>
                </div>

                <p class="metric-label">{{ card.title }}</p>
                <p class="metric-value">{{ formatValue(card.value) }}</p>
                <p class="metric-note">{{ card.note }}</p>
            </article>
        </section>

        <section class="chart-section">
            <div class="section-header">
                <div>
                    <p class="section-kicker">Analytics</p>
                    <h2>Activity overview</h2>
                </div>
                <p class="section-copy">
                    A quick view of the club's current activity across memberships and events.
                </p>
            </div>

            <div class="chart-card">
                <BarChart
                    :members="members"
                    :applications="applications"
                    :booking="booking"
                    :tournament="tournament"
                />
            </div>
        </section>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { Users, FileText, CalendarCheck, Trophy } from "lucide-vue-next";
import BarChart from "@/components/BarChart.vue";
import axios from "axios";

const isLoading = ref(true);
const errorMessage = ref("");
const lastSyncedAt = ref(null);

const members = ref(0);
const applications = ref(0);
const booking = ref(0);
const tournament = ref(0);

const cards = ref([
    {
        title: "Total Members",
        value: 0,
        icon: Users,
        tag: "Core growth",
        note: "Registered club members currently in the system."
    },
    {
        title: "Total Applications",
        value: 0,
        icon: FileText,
        tag: "Pipeline",
        note: "Applications awaiting review or processing."
    },
    {
        title: "Bookings",
        value: 0,
        icon: CalendarCheck,
        tag: "Operations",
        note: "Confirmed bookings for courts and facilities."
    },
    {
        title: "Tournaments",
        value: 0,
        icon: Trophy,
        tag: "Events",
        note: "Scheduled and active competition entries."
    }
]);

const lastSyncedLabel = computed(() => {
    if (!lastSyncedAt.value) {
        return isLoading.value ? "Waiting for data" : "Not available";
    }

    return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short"
    }).format(lastSyncedAt.value);
});

function handleResize() {
    // Reserved for responsive dashboard behavior.
}

function formatValue(value) {
    return new Intl.NumberFormat("en-US").format(Number(value) || 0);
}

async function loadDashboardAnalytics() {
    isLoading.value = true;
    errorMessage.value = "";

    try {
        const res = await axios.get("/dashboard_analytics.php");
        const data = res?.data?.data ?? {};

        members.value = data.totalMembers ?? 0;
        applications.value = data.totalApplications ?? 0;
        booking.value = data.bookings ?? 0;
        tournament.value = data.tournaments ?? 0;

        cards.value = [
            {
                title: "Total Members",
                value: members.value,
                icon: Users,
                tag: "Core growth",
                note: "Registered club members currently in the system."
            },
            {
                title: "Total Applications",
                value: applications.value,
                icon: FileText,
                tag: "Pipeline",
                note: "Applications awaiting review or processing."
            },
            {
                title: "Bookings",
                value: booking.value,
                icon: CalendarCheck,
                tag: "Operations",
                note: "Confirmed bookings for courts and facilities."
            },
            {
                title: "Tournaments",
                value: tournament.value,
                icon: Trophy,
                tag: "Events",
                note: "Scheduled and active competition entries."
            }
        ];

        lastSyncedAt.value = new Date();
    } catch (error) {
        errorMessage.value =
            "We couldn't load the latest dashboard data right now. Please try again in a moment.";
        console.error(error);
    } finally {
        isLoading.value = false;
    }
}

onMounted(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    loadDashboardAnalytics();
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", handleResize);
});
</script>

<style scoped>
.dashboard-shell {
    position: relative;
    min-height: 100vh;
    padding: 20px;
    background:
        radial-gradient(circle at top left, rgba(15, 91, 43, 0.08), transparent 30%),
        linear-gradient(180deg, #f8fafc 0%, #eef3f7 100%);
}

.dashboard-shell::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
        linear-gradient(rgba(15, 23, 42, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(15, 23, 42, 0.03) 1px, transparent 1px);
    background-size: 28px 28px;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.35), transparent 90%);
    pointer-events: none;
}

.hero-panel,
.chart-section,
.metric-card,
.status-banner {
    position: relative;
    z-index: 1;
}

.hero-panel {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    padding: 28px;
    margin-bottom: 24px;
    border: 1px solid rgba(148, 163, 184, 0.24);
    border-radius: 28px;
    background:
        linear-gradient(135deg, rgba(7, 43, 26, 0.98), rgba(15, 91, 43, 0.92)),
        #0f172a;
    color: #fff;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.16);
}

.eyebrow {
    margin: 0 0 10px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.hero-panel h1 {
    margin: 0;
    font-size: clamp(30px, 4vw, 46px);
    line-height: 1.05;
    letter-spacing: -0.03em;
}

.hero-copy {
    max-width: 700px;
    margin: 12px 0 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 16px;
    line-height: 1.7;
}

.hero-meta {
    display: grid;
    gap: 12px;
    min-width: min(100%, 240px);
}

.status-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    padding: 10px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.02em;
    backdrop-filter: blur(10px);
}

.status-pill.loading {
    background: rgba(255, 255, 255, 0.2);
}

.sync-card {
    padding: 16px 18px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.14);
}

.sync-card span {
    display: block;
    margin-bottom: 6px;
    color: rgba(255, 255, 255, 0.68);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
}

.sync-card strong {
    font-size: 14px;
    font-weight: 600;
}

.status-banner {
    margin-bottom: 20px;
    padding: 14px 16px;
    border-radius: 16px;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid transparent;
}

.status-banner.error {
    color: #991b1b;
    background: #fef2f2;
    border-color: #fecaca;
}

.status-banner.loading {
    color: #334155;
    background: #f8fafc;
    border-color: #dbeafe;
}

.card-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 18px;
    margin-bottom: 24px;
}

.metric-card {
    padding: 22px;
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.84);
    border: 1px solid rgba(148, 163, 184, 0.22);
    box-shadow: 0 16px 36px rgba(15, 23, 42, 0.06);
    backdrop-filter: blur(10px);
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease,
        border-color 0.2s ease;
}

.metric-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 42px rgba(15, 23, 42, 0.1);
    border-color: rgba(15, 91, 43, 0.18);
}

.metric-card__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 20px;
}

.icon-shell {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 16px;
    background: linear-gradient(135deg, #0f5b2b, #0a3d1d);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.icon {
    width: 24px;
    height: 24px;
    color: #fff;
}

.metric-tag {
    padding: 8px 12px;
    border-radius: 999px;
    background: #eaf4ee;
    color: #0f5b2b;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
}

.metric-label {
    margin: 0;
    color: #475569;
    font-size: 14px;
    font-weight: 600;
}

.metric-value {
    margin: 10px 0 8px;
    color: #0f172a;
    font-size: clamp(28px, 4vw, 40px);
    font-weight: 800;
    letter-spacing: -0.03em;
}

.metric-note {
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.6;
}

.chart-section {
    padding: 24px;
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.88);
    border: 1px solid rgba(148, 163, 184, 0.22);
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
    backdrop-filter: blur(10px);
}

.section-header {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
}

.section-kicker {
    margin: 0 0 8px;
    color: #0f5b2b;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.section-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: clamp(22px, 2.2vw, 30px);
    letter-spacing: -0.02em;
}

.section-copy {
    max-width: 520px;
    margin: 0;
    color: #64748b;
    font-size: 15px;
    line-height: 1.7;
}

.chart-card {
    padding: 18px;
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: 1px solid rgba(148, 163, 184, 0.16);
}

@media (min-width: 768px) {
    .dashboard-shell {
        padding: 28px;
    }

    .card-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 1100px) {
    .card-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}

@media (max-width: 900px) {
    .hero-panel,
    .section-header {
        align-items: flex-start;
        flex-direction: column;
    }
}

@media (max-width: 640px) {
    .dashboard-shell {
        padding: 14px;
    }

    .hero-panel,
    .chart-section {
        padding: 18px;
        border-radius: 22px;
    }

    .metric-card {
        padding: 18px;
    }
}
</style>
